import React from 'react';
import { Tag, Space } from '@bedrock/components';
import createFormItemRow from '@mpc/common/src/components/SForm/createFormItemRow';
import { SelectFormat } from '@mpc/common/src/components/SForm/SFormElement';
import POPOUserList from '@mpc/common/src/features/POPOUserList';
import { BaseUserVo, enums, IpCooperationPermitAction } from '#services/api';
import ProductInfoTable from './ProductInfoTable';
import OnlineDetailTable from './OnlineDetailTable';
import SecondaryOnlineDetailTable from './SecondaryOnlineDetailTable';
import IpResourceSelect from './IpResourceSelect';
import LegalComplianceReport from './LegalComplianceReport';
import BenefitConfigNew from './BenefitConfig';
import InsuranceAmountTable from './InsuranceAmountTable';
import { CoreTermTable, SolutionTable } from './NegotiationTable';
import PaymentPlanTable from './PaymentPlanTable';
import { AUTH_DURATION_OPTIONS, L2_AWARD_AND_AFTER_STATES } from './utils';
import EffectEvaluationTable from './EffectEvaluationTable';
import AssessorSelect from './AssessorSelect';
import { isNil } from 'lodash-es';

const fileSizeLimit = 200 * 1024 * 1024;
const tip = '单个文件不能超过200M';
// 法务报告附件：仅支持 doc/docx/pdf/jpg/png，≤10 个，单个 ≤100MB
const legalFileSizeLimit = 100 * 1024 * 1024;
const legalTip =
  '仅支持上传 doc、docx、pdf、jpg、png\n上传数量不超过10个\n大小不超过100MB';

// 用户多选 Select 格式化（镜像 MPCDemandFormModel）：store 存 {user_id,user_name,...}[]，
// 提交时 services 层取 user_id 转 number[]；valueToSelectKey 从 store 对象提取 user_id 匹配 option
const userSelectFormat: SelectFormat<BaseUserVo> = {
  optionsToValue: (value, options) => {
    const option = options.find((item) => item.value === value);
    return {
      id: value,
      value: value,
      user_id: value,
      user_name: option?.name,
      ...option,
    };
  },
  valueToSelectKey: (value) => {
    return value?.user_id || value?.value;
  },
};

// 人员字段只读态展示（POPOUserList 头像 + 姓名），镜像 MPCDemandFormModel
const userShowComponent = (value) => {
  const userList = value
    ?.map((n) => {
      return {
        ...n,
        name: n.user_name || n.name,
        user_id: n.user_id || n.value,
      };
    })
    .filter((n) => n.name);
  return <POPOUserList list={userList} />;
};

// key对齐 Figma 设计稿 node 36585:246905「新建IP合作需求」
const IPCooperationDemandFormModel = createFormItemRow({
  // ------------- 产品信息 -------------
  // 需求标题：详情态从外层 detail.title 读取（不在 l1_info 内），新建态读 formData.title
  title: {
    label: '需求标题',
    required: true,
    componentClass: 'textarea',
    col: 24,
    elementProps: {
      maxLength: 200,
      showCount: true,
      rows: 2,
      placeholder: '代号+产品+IP名+联动地区联动需求',
    },
  },
  // 出价方案环节才能追加」表面存在冲突，按 Figma 实现，待后端接口落地时与产品最终核对
  products: {
    label: '产品信息',
    required: true,
    col: 24,
    // 提交时联动校验内部 ProductInfoTable 各行「联动所属区域」必填（镜像 online_details watchChildFormValidate 范式）；
    // validateTrigger: false 使内层表格校验只在提交（form.validateFields）时触发，避免每次单元格变更就提前报错
    validateTrigger: false,
    watchChildFormValidate: true,
    rules: [
      {
        validator: (_rule, value) => {
          // 新建态初始 products 为 [{}]（length=1），默认 required 校验不生效，须校验至少一行含真实产品 id
          if (!Array.isArray(value) || !value.some((p) => p?.id)) {
            return Promise.reject(new Error('请选择产品'));
          }
          return Promise.resolve();
        },
      },
    ],
    render: (renderData: any, itemProps: any) => {
      // 受控模式：value/onChange 由 Form.Item 注入（对齐 OnlineDetailTable 范式），拆单带入默认值才能回显；
      return (
        <ProductInfoTable
          value={itemProps?.value}
          onChange={itemProps?.onChange}
          productOptions={renderData?.optionMap?.product?.options}
          // 发行区域选项（Apollo 动态下发）经统一 modelConf optionMap 下传，供内部渲染翻译 USA/RUSSIA 等扩展值
          countryRegionOptions={renderData?.optionMap?.country_region_options}
          // 产品上架平台选项（Apollo 动态下发）经统一 modelConf optionMap 下传，供内部渲染翻译平台 code→中文名
          launchPlatformOptions={renderData?.optionMap?.launch_platform_options}
          // 新建一级单仅允许 1 个产品：隐藏「新增产品」按钮与「操作」列；view 态（详情只读）单元格纯展示
          view={itemProps?.view}
          lockProduct={renderData?.detailData?.depth === 1}
        />
      );
    },
  },
  // ------------- IP联动信息 -------------
  // 需求人员（一级单）：新建态 select 编辑；详情态 formData.demand_users = l1_info.demand_users（UserSimpleVo[]）
  // 表单 Select 使用 selectFormat（userSelectFormat）存储 {user_id,user_name,...}[]，创建时提取 user_id → demand_user_ids
  // 二级单不显示此字段（改用 business_user_ids）
  demand_users: {
    label: '需求人员',
    show: ({ formData }: any) => formData?.depth !== 1,
    required: true,
    componentClass: 'select',
    col: 12,
    elementProps: (renderData: any) => {
      const { optionMap, formData } = renderData;
      return {
        multiple: true,
        showSearch: true,
        showConfirm: true,
        showReset: true,
        selectFormat: userSelectFormat,
        // 需求人员选项：随所选产品动态加载（key: ip_cooperation_demand_users，经 optionMap 注入）
        options: optionMap?.ip_cooperation_demand_users?.options,
        // 未选择产品时禁用（新建态 products 初始含 1 空行，需按是否已选 id 判断）
        disabled: !formData?.products?.some((p) => p?.id),
        renderLabel: (option: any) => {
          return `${option?.name} (${option?.email})`;
        },
        showComponent(value) {
          return userShowComponent(value);
        },
      };
    },
  },
  business_users: {
    label: '商务人员',
    componentClass: 'select',
    col: 12,
    elementProps: (renderData: any) => {
      const { optionMap, formData } = renderData;
      return {
        multiple: true,
        showSearch: true,
        showConfirm: true,
        showReset: true,
        selectFormat: userSelectFormat,
        // 需求人员选项：随所选产品动态加载（key: ip_cooperation_demand_users，经 optionMap 注入）
        options: optionMap?.ip_cooperation_demand_users?.options,
        // 未选择产品时禁用（新建态 products 初始含 1 空行，需按是否已选 id 判断）
        disabled: !formData?.products?.some((p) => p?.id),
        renderLabel: (option: any) => {
          return `${option?.name} (${option?.email})`;
        },
        showComponent(value) {
          return userShowComponent(value);
        },
      };
    },
  },
  // 商务人员（二级单）：编辑态 select；详情态 formData.business_user_ids = l1_info.business_users（UserSimpleVo[]）
  // 字段 key 对齐接口 business_user_ids（selectFormat 存储 {user_id,user_name,...}[]，提交时取 user_id 转 number[]）
  // 仅二级单展示，一级单不显示
  business_user_ids: {
    label: '商务人员',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'select',
    col: 12,
    elementProps: (renderData: any) => {
      const { optionMap, detailData } = renderData;
      return {
        multiple: true,
        showSearch: true,
        showConfirm: true,
        showReset: true,
        selectFormat: userSelectFormat,
        // 商务人员选项：随所选产品动态加载（key: ip_cooperation_business_users，经 optionMap 注入）
        options: optionMap?.ip_cooperation_business_users?.options,
        renderLabel: (option: any) => {
          return `${option?.name} (${option?.email})`;
        },
        showComponent(value) {
          return userShowComponent(value);
        },
        inputExtra: `建议多选同一部门项目成员以便后续共同查阅和操作流程；如发起前置审批，需求人员需至少选择一名正职员工。`,
      };
    },
  },
  // 虚拟键 $ip_name + formItemKeyMap：单选 IP 资源，同时回写 l1_info 的 ip_resource_id / ip_resource_snapshot（范式镜像 MPCDemandFormModel 的 $product）
  $ip_name: {
    label: 'IP名称（中英）',
    required: true,
    col: 12,
    formItemKeyMap: {
      id: 'ip_resource_id',
      name: 'ip_resource_snapshot.name',
      name_en: 'ip_resource_snapshot.name_en',
      ip_types: 'ip_resource_snapshot.ip_types',
    },
    rules: [
      {
        validator: (_rule, value: any) => {
          if (!value?.id) {
            return Promise.reject('请选择IP');
          }
          return Promise.resolve();
        },
      },
    ],
    render: (renderData: any, itemProps: any) => {
      const snapshot = renderData?.formData?.ip_resource_snapshot;
      return (
        <IpResourceSelect
          value={itemProps?.value}
          onChange={itemProps?.onChange}
          view={itemProps?.view}
          ipName={snapshot?.name || snapshot?.name_en}
        />
      );
    },
  },
  // 主IP类型：只读展示。选中 IP 后由 $ip_name.formItemKeyMap 将 ipresource 接口返回的
  // ip_types 回填到 ip_resource_snapshot.ip_type（后端返回 string[]，2026-08-07 由 IpTypeEnum[] 改为 string[]，
  // 选项来源 Apollo ip.types.options，通过 renderData.optionMap.ip_type 注入做 value→name 映射）
  ip_type: {
    label: 'IP类型',
    col: 12,
    className: 'h-full',
    render: ({ formData, optionMap }: any) => {
      const types: string[] = formData?.ip_resource_snapshot?.ip_types ?? [];
      if (!types.length)
        return (
          <span className="text-color-3 h-full flex items-center">--</span>
        );
      const opts: { name?: string; value?: string }[] =
        optionMap?.ip_type ?? [];
      return (
        <Space size={4} className={'h-full flex-wrap gap-y-1'}>
          {types.map((t) => {
            const option = opts.find((o) => o?.value === t);
            return (
              <Tag
                className={'whitespace-nowrap'}
                color={'light-grey'}
                key={t}
                size="small"
                text={option?.name || t}
              />
            );
          })}
        </Space>
      );
    },
  },
  // 附加合作 IP 类型（l1_info.extra_ip_types，MUSIC/DUBBING/ANIMATION_CUT）
  extra_ip_types: {
    label: '附加合作IP类型',
    componentClass: 'checkbox',
    col: 12,
    elementProps: {
      options: enums.IpCooperationExtraIpTypeEnum.values(),
      // 只读态：value 数组（如 ['MUSIC','DUBBING']）经枚举翻译 name 后逐个渲染 Tag（镜像 ip_type render 范式）
      showComponent: (values: any) => {
        if (!values?.length) return <span className="text-color-3">--</span>;
        const opts = enums.IpCooperationExtraIpTypeEnum.values();
        return (
          <Space size={4}>
            {values.map((v) => {
              const option = opts.find((o) => o.value === v);
              return (
                <Tag
                  className={'whitespace-nowrap'}
                  color={'light-grey'}
                  key={v}
                  size="small"
                  text={option?.name || v}
                />
              );
            })}
          </Space>
        );
      },
    },
  },
  cooperation_mode: {
    label: '合作方式',
    required: true,
    componentClass: 'radio',
    col: 12,
    elementProps: {
      options: enums.IpCooperationCooperationModeEnum.values(),
    },
  },
  // 需求总预算：currencytype（金额+币种一体），formItemKeyMap 拆分 total_budget + currency；
  // 提交接口 total_budget（BigDecimal）+ currency（对齐 IpCooperationCurrencyEnum 大写）；镜像 $estimated_amount 范式
  $total_budget: {
    label: '需求总预算',
    required: true,
    componentClass: 'currencytype',
    col: 12,
    formItemKeyMap: { value: 'total_budget', currency: 'currency' },
    rules: [
      {
        validator: (_rule, value: any) => {
          // formItemKeyMap 拆分后 required 默认校验对 {value,currency} 对象恒通过，须显式校验金额非空
          if (value?.value == null || value?.value === '') {
            return Promise.reject(new Error('请输入需求总预算'));
          }
          return Promise.resolve();
        },
      },
    ],
    elementProps: {
      block: true,
      selectStyle: { width: '120px' },
    },
  },
  cooperation_desc: {
    label: '合作需求明细',
    required: true,
    componentClass: 'textarea',
    col: 24,
    elementProps: {
      maxLength: 800,
      showCount: true,
      placeholder: '请填写',
    },
  },
  // 合作需求附件（一级单）：上传组件，提交映射 attachment_blob_ids（active_storage_blobs.id，取 blob.id），
  // 详情回显 cooperation_demand_attachs（AttachmentDto[]，name=cooperation_demand），非必填
  cooperation_demand_attachs: {
    label: '合作需求附件',
    componentClass: 'upload',
    col: 24,
    elementProps: {
      tip,
      fileSizeLimit,
      ellipsis: true,
    },
  },
  exclusive: {
    label: '独家需求',
    required: true,
    componentClass: 'radio',
    col: 24,
    elementProps: {
      options: enums.IpCooperationExclusiveEnum.values(),
    },
  },
  exclusion_list: {
    label: '排他名单',
    // 无独家需求时也展示（常显）；独家需求（exclusive===1）时必填（镜像 MPCDemandFormModel:830 函数式 required 范式）
    required: ({ formData }: any) => formData?.exclusive === 1,
    componentClass: 'textarea',
    col: 24,
    elementProps: {
      placeholder: '请输入排他名单',
      maxLength: 200,
      showCount: true,
    },
  },
  $first_exposure_at: {
    label: '首曝时间',
    // 特殊启宣需求选「是」才显示且必填（镜像 exclusion_list 函数式 required 范式）
    show: ({ formData }: any) =>
      formData?.special_promotion ===
      enums.IpCooperationSpecialPromotionEnum.YES,
    required: ({ formData }: any) =>
      formData?.special_promotion ===
      enums.IpCooperationSpecialPromotionEnum.YES,
    componentClass: 'datePicker',
    col: 12,
    formItemKeyMap: {
      '[0]': 'first_exposure_begin',
      '[1]': 'first_exposure_end',
    },
    rules: [
      {
        validator: (_rule, value) => {
          // formItemKeyMap 未选时重建为 ['','']，默认 required 校验不生效，须显式校验数组两元素
          if (!value || !Array.isArray(value) || !value[0] || !value[1]) {
            return Promise.reject(new Error('请选择首曝时间'));
          }
          return Promise.resolve();
        },
      },
    ],
    elementProps: {
      range: true,
      // 首曝时间仅需精确到月（对齐 online_details labelTip「无需精确到具体日期」）；
      // 不设 format，提交值保持 YYYY-MM-DD（月初）兼容后端 LocalDate
      picker: 'month',
      format: 'YYYY-MM',
    },
  },
  online_count: {
    label: '上线次数',
    col: 12,
    className: 'h-full',
    render: ({ formData, value }: any) => {
      // 优先读 Form 字段受控值（表格增删行实时回写），fallback 读 formData（兼容只读详情态）
      const count = value?.length ?? (formData?.online_details?.length || 0);
      return <span className={'h-full flex items-center'}>{count}</span>;
    },
  },
  online_details: {
    label: '预计联动周期',
    labelTip:
      '请选择预计联动月份，可填写IP上线前后3个月内的时间范围，无需精确到具体日期',
    required: true,
    // 提交时联动校验内部 OnlineDetailTable 各行「预计下线窗口晚于预计上线窗口」（镜像 MPCDemand cooperate_account_settle_list 范式）
    validateTrigger: false,
    watchChildFormValidate: true,
    col: 24,
    render: (_renderData: any, itemProps: any) => {
      // 显式透传 Form.Item 注入的受控 value/onChange，确保 OnlineDetailTable 以受控模式运行：
      // ProTableForm 内部 useControllableValue 受控时增/删行会触发 onChange 回写外层 Form 的
      // online_details 字段 → formData.online_details 同步 → online_count 联动刷新。
      // view 态（详情只读）：单元格纯展示
      return (
        <OnlineDetailTable
          view={itemProps?.view}
          // 新建态实时冲突预检需要当前所选IP（ip_resource_id）
          ipId={_renderData?.formData?.ip_resource_id}
        />
      );
    },
  },
  $marketing_promotion_time: {
    label: '营销启宣时间',
    // 特殊启宣需求选「否」才显示且必填（镜像 $first_exposure_at 选「是」显示）
    show: ({ formData }: any) =>
      formData?.special_promotion ===
      enums.IpCooperationSpecialPromotionEnum.NO,
    required: ({ formData }: any) =>
      formData?.special_promotion ===
      enums.IpCooperationSpecialPromotionEnum.NO,
    componentClass: 'datePicker',
    col: 12,
    formItemKeyMap: {
      '[0]': 'marketing_promotion_begin',
      '[1]': 'marketing_promotion_end',
    },
    rules: [
      {
        validator: (_rule, value) => {
          // formItemKeyMap 未选时重建为 ['','']，默认 required 校验不生效，须显式校验数组两元素
          if (!value || !Array.isArray(value) || !value[0] || !value[1]) {
            return Promise.reject(new Error('请选择营销启宣时间'));
          }
          return Promise.resolve();
        },
      },
    ],
    elementProps: {
      range: true,
      // 营销启宣时间仅需精确到月（存月首）；picker=month + format=YYYY-MM 对齐接口营销启宣开始/结束契约
      picker: 'month',
      format: 'YYYY-MM',
    },
  },
  special_promotion: {
    label: '特殊启宣需求',
    required: true,
    componentClass: 'radio',
    col: 12,
    // 是/否下方文字说明（镜像 MPCDemandFormModel member_planners inputExtra 范式，text-color-3 小字）
    inputExtra: () => (
      <div className="text-xs text-color-3">
        需要在特别节点（例如在年度发布会、周年庆等），通常远远早于（2个月以上）联动上线时间进行首曝
      </div>
    ),
    elementProps: {
      options: enums.IpCooperationSpecialPromotionEnum.values(),
    },
  },
  // 特别节点：特殊启宣需求选「是」才显示（镜像 $first_exposure_at 谓词）；文本框≤20字，非必填。
  // itemCols 中须排在 $first_exposure_at / $marketing_promotion_time 之后，才能落到第二行（对齐设计稿）
  special_node: {
    label: '特别节点',
    show: ({ formData }: any) =>
      formData?.special_promotion ===
      enums.IpCooperationSpecialPromotionEnum.YES,
    componentClass: 'input',
    col: 12,
    elementProps: {
      placeholder: '请输入特别节点',
      maxLength: 20,
    },
  },
  // ------------- 评估报告 -------------
  // 新/复投IP：对齐 l1_info.new_ip_resource（0=复投IP 1=新IP，IpCooperationNewIpEnum）
  new_ip_resource: {
    label: '新/复投IP',
    required: true,
    componentClass: 'radio',
    col: 12,
    elementProps: {
      options: enums.IpCooperationNewIpEnum.values(),
      // 只读态渲染为 Tag（设计稿 Filled-light：新IP 浅蓝 / 复投IP 灰）
      showComponent: (value: any) => {
        const option = enums.IpCooperationNewIpEnum.values().find(
          (o) => o.value === value
        );
        if (!option) return <span className="text-color-3">--</span>;
        // value=1 新IP 浅蓝；value=0 复投IP 灰
        return (
          <Tag
            size="xSmall"
            text={option.name}
            color={value === 1 ? 'light-blue' : 'light-grey'}
          />
        );
      },
    },
  },
  // 评估方：对齐 l1_info.evaluator（BUSINESS_SELF=游戏产品项目组/SDC=商业分析与合规部/UX=用户体验中心/CREATIVE=在线游戏事业部-创意部）
  evaluator: {
    label: '评估方',
    required: true,
    componentClass: 'select',
    col: 12,
    elementProps: ({ formData }: any) => {
      let options = enums.IpCooperationEvaluatorEnum.values();
      if (formData?.new_ip_resource === 1 || isNil(formData?.new_ip_resource)) {
        options = options.filter(
          (t) => t.value !== enums.IpCooperationEvaluatorEnum.BUSINESS_SELF
        );
      }

      return {
        placeholder: '请选择',
        options,
      };
    },
  },
  // 评估报告-评估人：MPC 用户管理库多选（AssessorSelect → UserSelect，对齐接口 l1_info.evaluation_users）。
  // 展示/必填范围由场景决定：新建抽屉（无 detailData）仅「复投IP + 游戏产品项目组自估」采集且必填
  // （对齐 evaluator 选项过滤与 effect_evaluation 谓词）；详情态无场景限制恒展示（接口 l1_info.evaluation_users
  // 注释亦仅约束「未提交报告前可编辑」，无场景条件），可编辑性由调用方经 itemCols view 控制
  // （详情页 = !evaluationEditable，即未提交评估报告前可编辑、提交后只读）
  evaluation_users: {
    label: '评估人',
    required: true,
    show: ({ formData, detailData }: any) => {
      // depth 守卫：一级单字段，避免二级单误挂（与 legal_users 同范式）
      if (detailData?.depth === 1) return false;
      // 创建表单无 detailData → 视为 NEW 态；详情态按 state.code 判断
      // （NEW 含「创建后退回」的草稿态，此场景同样只按场景谓词展示，不能当恒展示处理）
      const isNewState =
        !detailData ||
        detailData?.state?.code === enums.IpCooperationDemandStateEnum.NEW;
      // 非 NEW 态：恒展示（接口 l1_info.evaluation_users 仅约束「未提交报告前可编辑」，无场景条件）
      if (!isNewState) return true;
      // NEW 态：仅「复投IP + 游戏产品项目组自估」场景采集且必填（对齐 effect_evaluation / evaluator 选项过滤）
      return (
        formData?.new_ip_resource === enums.IpCooperationNewIpEnum.RE_INVEST &&
        formData?.evaluator === enums.IpCooperationEvaluatorEnum.BUSINESS_SELF
      );
    },
    // col 12：与需求人员/商务人员等人员选择字段同宽（半幅），避免 UserSelect 撑满整行
    col: 12,
    render: (_renderData: unknown, itemProps: { view?: boolean }) => (
      <AssessorSelect view={itemProps?.view} />
    ),
  },
  // 效果评估（复投IP游戏产品项目组自估在新建/一级单详情编辑填写；非游戏产品项目组自估在详情由评估方提交）。
  // 表格 = 联动序号（派生自 online_details「预计联动周期」数量）+ 效果指标/效果预期（addGroup 子表）+ 评估报告附件（每联动序号行一个上传）
  effect_evaluation: {
    label: '效果评估',
    show: ({ formData, detailData }: any) => {
      // 创建表单无 detailData → 视为 NEW 态；详情态按 state.code 判断
      const isNewState =
        !detailData ||
        detailData?.state?.code === enums.IpCooperationDemandStateEnum.NEW;
      if (!isNewState) {
        // 非 NEW 态：已有已保存效果评估才展示（只读或评估方提交后）；l1_info 已改由 effect_evaluation_groups 返回
        return (
          !!detailData?.l1_info?.effect_evaluation_groups?.length ||
          detailData?.permit_actions?.includes(
            enums?.IpCooperationPermitActionEnum?.EVALUATION
          )
        );
      }
      // NEW 态：复投IP + 游戏产品项目组自估 时展示可编辑（对齐 evaluator 选项过滤，新IP 不可选游戏产品项目组自估）
      return (
        formData?.new_ip_resource === enums.IpCooperationNewIpEnum.RE_INVEST &&
        formData?.evaluator === enums.IpCooperationEvaluatorEnum.BUSINESS_SELF
      );
    },
    // 提交时联动校验内部 EffectEvaluationTable 各效果指标/效果预期必填（镜像 online_details watchChildFormValidate 范式）；
    // validateTrigger: false 使内层表格校验只在提交（form.validateFields）时触发，避免每次单元格变更就提前报错
    validateTrigger: false,
    watchChildFormValidate: true,
    col: 24,
    render: (renderData: any, itemProps: any) => (
      <EffectEvaluationTable
        view={itemProps?.view}
        // 联动序号行数 = 预计联动周期行数（online_details.length）；新建/详情编辑态随其增删实时调和
        onlineDetailsCount={renderData?.formData?.online_details?.length ?? 0}
      />
    ),
  },

  // ------------- 法务合规报告 -------------
  // 是否存在法务风险（0=无风险 1=有风险，对齐 l1_info.has_legal_risk / IpCooperationLegalRiskEnum）
  has_legal_risk: {
    label: '是否存在法务风险',
    required: true,
    componentClass: 'radio',
    show: ({ formData, detailData }: any) =>
      detailData?.state?.code !== enums.IpCooperationDemandStateEnum.NEW,
    col: 24,
    elementProps: {
      options: enums.IpCooperationLegalRiskEnum.values(),
      // 只读态渲染为 Tag（有风险红 / 无风险灰，对齐 new_ip_resource showComponent 范式）
      showComponent: (value: any) => {
        const option = enums.IpCooperationLegalRiskEnum.values().find(
          (o) => o.value === value
        );
        if (!option) return <span className="text-color-3">--</span>;
        // value=1 有风险红；value=0 无风险灰
        return (
          <Tag
            size="xSmall"
            text={option.name}
            color={value === 1 ? 'light-red' : 'light-grey'}
          />
        );
      },
    },
  },
  // 法务合规报告-评估人：MPC 用户管理库多选（AssessorSelect → UserSelect，对齐接口 l1_info.legal_users）。
  // 仅在详情态随「法务合规报告」模块展示（新建抽屉无该模块字段）；show 谓词镜像同模块 has_legal_risk
  // （非 NEW 态才展示），depth 守卫避免二级单误挂。可编辑性由调用方经 itemCols view 控制
  // （详情页 = !legalReportEditable，即未提交法务合规报告前可编辑、提交后只读）
  legal_users: {
    label: '评估人',
    required: true,
    show: ({ detailData }: any) =>
      !!detailData &&
      detailData?.depth !== 1 &&
      detailData?.state?.code !== enums.IpCooperationDemandStateEnum.NEW,
    // col 12：与需求人员/商务人员等人员选择字段同宽（半幅），避免 UserSelect 撑满整行
    col: 12,
    render: (_renderData: unknown, itemProps: { view?: boolean }) => (
      <AssessorSelect view={itemProps?.view} />
    ),
  },
  // 法务风险记录（多条）：受控编辑表格，风险为是时展示（对齐 OnlineDetailTable value/onChange 范式）
  legal_risk_records: {
    label: '法务风险记录',
    show: ({ formData, detailData }: any) =>
      formData?.has_legal_risk === 1 &&
      detailData?.state?.value !== enums.IpCooperationDemandStateEnum.NEW,
    // 提交时联动校验内部 LegalComplianceReport 各行风险类型/风险等级/状态必填（镜像 online_schedule watchChildFormValidate 范式）；
    // validateTrigger: false 使内层表格校验只在提交（form.validateFields）时触发，避免每次单元格变更就提前报错
    validateTrigger: false,
    watchChildFormValidate: true,
    col: 24,
    render: (_renderData: any, itemProps: any) => (
      <LegalComplianceReport
        value={itemProps?.value}
        onChange={itemProps?.onChange}
        view={itemProps?.view}
      />
    ),
  },
  // 法务报告附件：风险为是则必填（经 active_storage_attachments 关联 name=legal_report）
  legal_report_attachs: {
    label: '法务报告附件',
    show: ({ formData, detailData }: any) =>
      formData?.has_legal_risk === 1 &&
      detailData?.state?.value !== enums.IpCooperationDemandStateEnum.NEW,
    required: true,
    componentClass: 'upload',
    col: 24,
    elementProps: {
      tip: legalTip,
      fileSizeLimit: legalFileSizeLimit,
      ellipsis: true,
    },
  },

  // ============================================================
  // 二级单（出价方案 / 权益明细）专属字段
  // 所有字段均挂 show 谓词：仅当 formData.depth === 1 时渲染
  // TODO(24005): 二级单接口未落地，所有字段值为占位，后续按真实字段命名/枚举对齐
  // ============================================================

  // ----- 产品信息 Tab（字段 key 对齐接口 multi_product_merge）-----
  multi_product_merge: {
    label: '多产品合并',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'radio',
    col: 12,
    elementProps: {
      options: [
        { name: '是', value: true },
        { name: '否', value: false },
      ],
    },
  },

  // ----- 出价方案 Tab -----
  copyright_owner_name: {
    label: '版权方',
    show: ({ detailData }: any) => detailData?.depth === 1,
    col: 12,
    render: ({ detailData }: any) => {
      const value = detailData?.copyright_owner_name;
      if (!value) return <span className="text-color-3">--</span>;
      return <span>{value}</span>;
    },
  },
  ip_difficulty: {
    label: '联动难度',
    show: ({ detailData }: any) => detailData?.depth === 1,
    col: 12,
    render: ({ detailData, optionMap }: any) => {
      const value = detailData?.ip_difficulty;
      if (!value) return <span className="text-color-3">--</span>;
      const matched = (optionMap?.cooperation_difficulty_options ?? []).find(
        (o: any) => o?.value === value
      );
      const colorMap = {
        LOW: 'light-green',
        MEDIUM: 'light-orange',
        HIGH: 'light-red',
      };
      return (
        <Tag
          color={colorMap[value] || 'light-grey'}
          size="small"
          text={matched?.name || value}
        />
      );
    },
  },
  // 附加IP类型（出价方案设计，联动难度后）：单选，必填；选「音乐」→ 展示下方音乐权益模块
  // （区别于一级单 extra_ip_types 多选 checkbox，此字段为二级单出价方案单选开关，接口未开发 mock 契约）
  extra_ip_type: {
    label: '附加IP类型',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'radio',
    col: 12,
    elementProps: {
      options: enums.IpCooperationExtraIpTypeEnum.values(),
    },
  },

  // 授权音乐数量（二级单音乐权益模块只读展示；由 music_benefit 数组长度派生，联动歌曲 Tab 数量）
  music_benefit_count: {
    label: '授权音乐数量',
    col: 12,
    render: ({ formData, detailData }: any) => {
      const musicBenefit = formData?.music_benefit;
      const count = Array.isArray(musicBenefit)
        ? musicBenefit.length
        : (detailData?.music_benefit_count ?? 1);
      return <span style={{ lineHeight: '28px' }}>{count}</span>;
    },
  },

  // 虚拟键 $estimated_amount + formItemKeyMap：currencytype 输出 {value, currency} 拆分为接口字段 estimated_amount + currency
  $estimated_amount: {
    label: '预估金额',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'currencytype',
    col: 12,
    formItemKeyMap: { value: 'estimated_amount', currency: 'currency' },
    elementProps: {
      block: true,
      selectStyle: { width: '120px' },
    },
  },

  // ----- 费用预算（出价方案卡片内 ProTablePlus 表格单元格字段，字段 key 对齐接口 IpCooperationL2DesignForm）-----
  // 字段经 BudgetOfferTable 单元格 renderType="row" + itemCols noStyle 直连外层 form 渲染；
  // LF/MG/Royalty 勾选态（singlecheckbox）控制报价列单元格显隐（未勾选不渲染金额字段 → 天然跳过必填校验）。
  // 币种统一走接口 currency（预算总金额/授权金 LF/保底金 MG 同币种），不设独立币种字段。
  $budget_total_amount: {
    label: '预算总金额',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'currencyInput',
    col: 12,
    formItemKeyMap: {
      value: 'estimated_amount',
      currency: 'currency',
    },
    // 校验逻辑（至少勾选一种授权费类型 + LF/MG 金额之和 = 预算总金额）由 BudgetOfferTable 内 hidden Form.Item 承载，
    // 此处仅保留预算总金额非空必填校验
    rules: [
      {
        validator: (_rule, value: any) => {
          // formItemKeyMap 拆分后 required 默认校验对 {value,currency} 对象恒通过，须显式校验金额非空
          if (value?.value == null || value?.value === '') {
            return Promise.reject(new Error('请输入预算总金额'));
          }
          return Promise.resolve();
        },
      },
    ],
    elementProps: {
      block: true,
      selectStyle: { width: '120px' },
    },
  },
  enable_license_fee: {
    label: '授权金（LF）',
    show: ({ detailData }: any) => detailData?.depth === 1,
    componentClass: 'singlecheckbox',
    col: 12,
    elementProps: {
      // singlecheckbox 文字走 children（label 在单元格内被 ip-cooperation-demand-core-term-form-item 隐藏）
      children: '授权金（LF）',
    },
  },
  // 虚拟键 $budget_lf_amount + formItemKeyMap：currencytype 输出 {value,currency} 拆分为接口字段 license_fee_amount + 共用币种 currency
  $budget_lf_amount: {
    label: '授权金报价',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'currencyInput',
    col: 12,
    formItemKeyMap: {
      value: 'license_fee_amount',
      currency: 'currency',
    },
    rules: [
      {
        validator: (_rule, value: any) => {
          if (value?.value == null || value?.value === '') {
            return Promise.reject(new Error('请输入授权金报价'));
          }
          return Promise.resolve();
        },
      },
    ],
    elementProps: {
      block: true,
      // 币种跟随预算总金额且不可修改（预算总金额币种切换时外层 useEffect 同步）
      selectProps: { disabled: true },
      inputProps: { min: 0.01, placeholder: '请输入' },
    },
  },
  enable_min_guarantee: {
    label: '保底金（MG）',
    show: ({ detailData }: any) => detailData?.depth === 1,
    componentClass: 'singlecheckbox',
    col: 12,
    elementProps: {
      // singlecheckbox 文字走 children（label 在单元格内被 ip-cooperation-demand-core-term-form-item 隐藏）
      children: '保底金（MG）',
    },
  },
  // 虚拟键 $budget_mg_amount + formItemKeyMap：currencytype 输出 {value,currency} 拆分为接口字段 min_guarantee_amount + 共用币种 currency
  $budget_mg_amount: {
    label: '保底金报价',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'currencyInput',
    col: 12,
    formItemKeyMap: {
      value: 'min_guarantee_amount',
      currency: 'currency',
    },
    rules: [
      {
        validator: (_rule, value: any) => {
          if (value?.value == null || value?.value === '') {
            return Promise.reject(new Error('请输入保底金报价'));
          }
          return Promise.resolve();
        },
      },
    ],
    elementProps: {
      block: true,
      selectStyle: { width: '120px' },
      // 币种跟随预算总金额且不可修改（预算总金额币种切换时外层 useEffect 同步）
      selectProps: { disabled: true },
      inputProps: { min: 0.01, placeholder: '请输入' },
    },
  },
  enable_royalty: {
    label: '分成（Royalty）',
    show: ({ detailData }: any) => detailData?.depth === 1,
    componentClass: 'singlecheckbox',
    col: 12,
    elementProps: {
      // singlecheckbox 文字走 children（label 在单元格内被 ip-cooperation-demand-core-term-form-item 隐藏）
      children: '分成（Royalty）',
    },
  },
  royalty_type: {
    label: '分成方式',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'select',
    col: 12,
    elementProps: {
      options: enums?.IpCooperationRoyaltyTypeEnum?.values(),
      placeholder: '请选择',
      showSearch: true,
    },
  },
  royalty_rate: {
    label: '分成占比',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    // 总分成费率（royalty_rate）改小数口径（0.2=20%）：百分比输入框展示 20%、form 存小数 0.2，对齐接口/后端
    componentClass: 'inputPercent',
    col: 12,
    elementProps: {
      min: 0,
      max: 1,
      digits: 2,
      valueType: 'number',
      placeholder: '请输入占比',
    },
  },
  license_fee_form: {
    label: '授权费形式',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'select',
    col: 12,
    elementProps: {
      options: enums?.IpLicenseFeeFormEnum?.values(),
      placeholder: '请选择',
      showSearch: true,
    },
  },
  payment_terms: {
    label: '付款条件',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'select',
    col: 12,
    elementProps: {
      options: enums?.IpCooperationPaymentTermsEnum?.values(),
      placeholder: '请选择',
      showSearch: true,
      className: 'w-full',
    },
  },
  license_regions: {
    label: '授权地区',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'select',
    col: 12,
    elementProps: (renderData: any) => ({
      // 授权地区选项：Apollo region.options 动态下发（经 optionMap.country_region_options 注入，镜像联动所属区域）
      options: renderData?.optionMap?.country_region_options || [],
      multiple: true,
      placeholder: '请选择',
      showSearch: true,
      className: 'w-full',
      type: 'label',
    }),
  },
  auth_duration: {
    label: '授权时长',
    show: ({ detailData }: any) => detailData?.depth === 1,
    required: true,
    componentClass: 'select',
    col: 12,
    elementProps: {
      options: AUTH_DURATION_OPTIONS,
      placeholder: '请选择',
      showSearch: true,
      // 出价列单元格内撑满（对齐 license_regions 的 w-full；授权信息表出价列编辑态输入 100% 宽）
      className: 'w-full',
    },
  },
  online_schedule: {
    label: '上线联动明细',
    show: ({ detailData }: any) => detailData?.depth === 1,
    // 提交时联动校验内部 SecondaryOnlineDetailTable 各行「联动上线时长」必填（镜像 online_details watchChildFormValidate 范式）；
    // validateTrigger: false 使内层表格校验只在提交（form.validateFields）时触发，避免每次单元格变更就提前报错
    validateTrigger: false,
    watchChildFormValidate: true,
    col: 24,
    render: (_renderData: any, itemProps: any) => {
      // 受控模式：拆单带入的一级单上线联动明细才能回显（对齐 OnlineDetailTable 范式）
      return (
        <SecondaryOnlineDetailTable
          value={itemProps?.value}
          onChange={itemProps?.onChange}
          view={itemProps?.view}
        />
      );
    },
  },

  // 权益明细：受控编辑表格，options/benefits 经 renderData 注入（对齐 SecondaryOnlineDetailTable / LegalComplianceReport value/onChange 范式）
  benefit_rows: {
    label: '权益明细',
    show: ({ detailData }: any) => detailData?.depth === 1,
    // 内部 ProTableForm 表单委托校验（镜像 online_details 范式）：提交时父表单 validateFields 联动校验
    // 各开启分组权益 attr 的必填规则；validateTrigger:false 避免值变化时频繁触发内部校验
    validateTrigger: false,
    watchChildFormValidate: true,
    col: 24,
    render: (renderData: any, itemProps: any) => (
      <BenefitConfigNew
        options={renderData?.benefitOptions}
        // 只读态（view）下 Form.Item 不注入受控 value（itemProps.value 为 undefined），
        // 详情回填值经 form.setFieldsValue→useSForm updateFormData 同步到 formData，用 formData.benefit_rows 兜底回显；
        // 编辑态 itemProps.value 受控优先，不受影响
        view={itemProps?.view}
        formData={renderData?.formData}
        mode={renderData?.benefitMode}
        // 谈判视角（showNegotiationColumns）追加「回复/意见反馈」列：items=BENEFIT section negotiation_items，
        // 行数据已由内包 buildFormValues 注入 action/opinion，经 ActionCell/OpinionCell 优先读行字段回显
        negotiation={
          renderData?.showNegotiationColumns
            ? {
                items: renderData?.negotiationItems,
                editable: renderData?.negotiation?.editable,
                // 场景 mode 透传（内包商务修改='internal' → BenefitConfigNew 回复/意见列只读；外包='external' → 可编辑）
                mode: renderData?.negotiation?.mode,
              }
            : undefined
        }
      />
    ),
  },
  // ----- 产品信息（外包详情只读展示，从产品信息模块选字段单独 formmodel 展示）-----
  // 合作IP：对齐详情 l2_info.ip_resource_snapshot.name（外包版权方视角）
  cooperation_ip: {
    label: '合作IP',
    show: ({ detailData }: any) => detailData?.depth === 1,
    col: 12,
    render: ({ formData }: any) => {
      const snapshot = formData?.ip_resource_snapshot;
      const name = snapshot?.name || snapshot?.name_en;
      return name ? (
        <span>{name}</span>
      ) : (
        <span className="text-color-3">--</span>
      );
    },
  },
  // 合作产品信息：对齐详情 l2_info.products[0].product_snapshot.name / identifier
  cooperation_product: {
    label: '合作产品信息',
    show: ({ detailData }: any) => detailData?.depth === 1,
    col: 12,
    render: ({ formData }: any) => {
      const p = (formData?.products || [])[0];
      return p ? (
        <span>{p?.name || p?.identifier || '--'}</span>
      ) : (
        <span className="text-color-3">--</span>
      );
    },
  },
  // 联动所属区域：对齐详情 l2_info.products[].linked_region（code→中文名翻译，对齐 Apollo region.options）
  linked_region: {
    label: '联动所属区域',
    show: ({ detailData }: any) => detailData?.depth === 1,
    col: 12,
    render: (renderData: any) => {
      const formData = renderData?.formData;
      const regions: string[] = (formData?.products || []).reduce<string[]>(
        (acc, p) => {
          (p?.linked_release_region || []).forEach((r: string) => {
            if (r && !acc.includes(r)) acc.push(r);
          });
          return acc;
        },
        []
      );
      if (!regions.length) return <span className="text-color-3">--</span>;
      const opts = renderData?.optionMap?.country_region_options || [];
      const text = regions
        .map((r) => opts.find((o: any) => o?.value === r)?.name || r)
        .join('/');
      return <span>{text}</span>;
    },
  },
  // 联动上架平台：镜像 linked_region 聚合展示（联动所属区域的上架平台版本，对齐详情 products[].linked_platform）
  linked_platform: {
    label: '联动上架平台',
    show: ({ detailData }: any) => detailData?.depth === 1,
    col: 12,
    render: ({ formData }: any) => {
      const platforms: string[] = (formData?.products || []).reduce<string[]>(
        (acc, p) => {
          (p?.linked_platform || []).forEach((r: string) => {
            if (r && !acc.includes(r)) acc.push(r);
          });
          return acc;
        },
        []
      );
      if (!platforms.length) return <span className="text-color-3">--</span>;
      // 上架平台无枚举翻译（对齐产品上架平台列直接展示），去重后 join
      return <span>{platforms.join('/')}</span>;
    },
  },

  // ============================================================
  // IP合作总金额「权益金额分配」专属字段（24005-B Offer确认金额分配）
  // 所有字段挂 show 谓词：仅二级单（depth===1）且处于 OA定标审批及之后（或 Offer确认草稿态 offerConfirmDraft）时渲染
  // 字段 key 已对齐接口契约 IpCooperationAmountAllocationDto：ip_cooperation_details（权益）
  // ============================================================

  // ----- 权益金额分配：我方主体×对方主体×金额 + 付款方式（ProTablePlusForm 两层 addGroup，复用 InsuranceAmountTable）-----
  ip_cooperation_details: {
    label: null,
    show: ({ detailData }: any) =>
      detailData?.depth === 1 &&
      (L2_AWARD_AND_AFTER_STATES.includes(detailData?.state?.code) ||
        detailData?.offerConfirmDraft),
    // 内部 ProTableForm 表单委托校验（镜像 benefit_rows watchChildFormValidate 范式）：
    // 提交时父表单 validateFields 联动校验各必填列；validateTrigger:false 避免值变化频繁触发内部校验
    validateTrigger: false,
    watchChildFormValidate: true,
    col: 24,
    labelStyle: { display: 'none' },
    render: ({ detailData, optionMap, formData }: any, itemProps: any) => {
      return (
        <InsuranceAmountTable
          view={itemProps?.view}
          // 我方主体选项：外层经 optionMap.our_party_options 注入（SecondaryDetailContent 用 useDemandOptions 按 ip_cooperation_our_party 字典 key 拉取）
          ourPartyOptions={optionMap?.our_party_options ?? []}
          // 谈判币种 = 预估总金额币种（formData.currency，来自 $estimated_amount formItemKeyMap 拆分）；
          // 预估总金额（formData.estimated_amount）供内部 decimal.js 合计校验
          negotiationCurrency={formData?.currency}
          estimatedTotalAmount={formData?.estimated_amount}
          // 付款类型可选项 = 费用预算勾选过滤（enable_license_fee/enable_min_guarantee/enable_royalty，
          // 镜像 payment_plan budgetConfig 联动范式）
          paymentBudgetConfig={{
            lfEnabled: !!detailData?.enable_license_fee,
            mgEnabled: !!detailData?.enable_min_guarantee,
            royaltyEnabled: !!detailData?.enable_royalty,
          }}
        />
      );
    },
  },
  // 备注：IP合作总金额卡片内权益模块下方展示（descriptions 风格独立实例渲染，对齐 offerConfirm 卡片字段）
  confirm_remark: {
    label: '备注说明',
    show: ({ detailData }: any) =>
      detailData?.depth === 1 &&
      (L2_AWARD_AND_AFTER_STATES.includes(detailData?.state?.code) ||
        detailData?.offerConfirmDraft),
    componentClass: 'textarea',
    col: 24,
    elementProps: {
      maxLength: 500,
      showCount: true,
      placeholder: '请填写',
      rows: 2,
    },
  },
  // 是否提供保险：对齐详情 l2_info.need_insurance（0/1 number，IpCooperationDemandDetailVoL2Info）
  need_insurance: {
    label: '是否提供保险',
    show: ({ detailData }: any) => detailData?.depth === 1,
    componentClass: 'radio',
    col: 12,
    elementProps: {
      options: [
        { name: '是', value: 1 },
        { name: '否', value: 0 },
      ],
    },
  },
  // 附件上传：对齐详情 l2_info.negotiation_attachments（AttachmentInfo[]，谈判轮次附件；提交时转 blob_ids）
  negotiation_attachments: {
    label: 'IP合作附件',
    show: ({ detailData }: any) => detailData?.depth === 1,
    componentClass: 'upload',
    col: 24,
    elementProps: {
      tip,
      fileSizeLimit,
      ellipsis: true,
    },
  },

  // ============================================================
  // 谈判反馈：核心商务条款 + IP合作游戏方详情（谈判表格，ProTablePlusForm 布局）
  // 谈判环境参数经 renderData.negotiation 鸭子注入（对齐 benefit_rows 经 renderData.benefitOptions 注入范式）：
  //   { editState, editable, onEditChange, onBatchAction }
  // items/l2 从 renderData.detailData.l2_info 读取（调用方须在 renderData.detailData 顶层透传 l2_info）。
  // 调用方加载：renderType='row' + itemCols=[{formItemKey, noStyle:true}]（镜像内包 benefit_rows 加载方式），
  // ProCard 标题承载模块名，字段 label 不显示。
  // ============================================================
  core_term_table: {
    label: '核心商务条款',
    show: ({ detailData }: any) => detailData?.depth === 1,
    col: 24,
    render: (renderData: any) => {
      const l2 = renderData?.detailData?.l2_info;
      const n = renderData?.negotiation;
      return (
        <CoreTermTable
          items={l2?.negotiation_items}
          l2={l2}
          editState={n?.editState}
          editable={n?.editable}
          onEditChange={n?.onEditChange}
          onBatchAction={n?.onBatchAction}
          countryRegionOptions={renderData?.optionMap?.country_region_options}
          mode={n?.mode}
        />
      );
    },
  },
  solution_table: {
    label: 'IP合作游戏方详情',
    show: ({ detailData }: any) => detailData?.depth === 1,
    // 提交时联动校验内部 SolutionTable 各必填列（auth_duration/duration_value/online_period）——
    // 镜像 ip_cooperation_details watchChildFormValidate 范式；validateTrigger:false 避免每次值变化频繁触发内部校验
    validateTrigger: false,
    watchChildFormValidate: true,
    col: 24,
    render: (renderData: any, itemProps: any) => {
      const l2 = renderData?.detailData?.l2_info;
      const n = renderData?.negotiation;
      // ⚠️ 必须透传 Form.Item 注入的受控 value/onChange：SolutionTable 是受控表格，缺 value 会渲染"暂无数据"。
      // 对齐其它受控表格（online_details/legal_risk_records 等 value/onChange 范式）
      return (
        <SolutionTable
          value={itemProps?.value}
          onChange={itemProps?.onChange}
          items={l2?.negotiation_items}
          l2={l2}
          editState={n?.editState}
          editable={n?.editable}
          onEditChange={n?.onEditChange}
          onBatchAction={n?.onBatchAction}
          mode={n?.mode}
        />
      );
    },
  },
  // 付款计划（2026 Q1 新增）：受控编辑表格，付款节点主行 → 付款方式子行（ProTablePlusForm addGroup 两层）；
  // 谈判视角（showNegotiationColumns + renderData.paymentPlanBudgetConfig）追加回复/意见列 + 费用预算联动；
  // 提交时联动校验内部 PaymentPlanTable 必填列 + 跨行汇总（LF/MG 金额=费用预算、Royalty 占比=100%，镜像 solution_table watchChildFormValidate 范式）
  // form 字段 key 对齐接口定义 payment_plan（提交/回显由 services/utils format 层转为 PaymentPlanItem[]）
  payment_plan: {
    label: null,
    show: ({ detailData }: any) => detailData?.depth === 1,
    // 内部 ProTablePlusForm 表单委托校验（镜像 solution_table 范式）：提交时父表单 validateFields 联动校验
    validateTrigger: false,
    watchChildFormValidate: true,
    col: 24,
    labelStyle: { display: 'none' },
    render: (renderData: any, itemProps: any) => {
      const l2 = renderData?.detailData?.l2_info;
      const n = renderData?.negotiation;
      const formData = renderData?.formData;

      const paymentPlanBudgetConfig = {
        lfEnabled: !!formData?.enable_license_fee,
        mgEnabled: !!formData?.enable_min_guarantee,
        royaltyEnabled: !!formData?.enable_royalty,
        lfAmount: formData?.license_fee_amount,
        mgAmount: formData?.min_guarantee_amount,
      };

      return (
        <PaymentPlanTable
          // 谈判视角：negotiation_items 回复/意见回显（镜像 solution_table items 透传）
          items={l2?.negotiation_items}
          // 编辑态优先级：内包表单视角走 paymentPlanEditable（view 门控，行为不变）；
          // 外包谈判视角未设 paymentPlanEditable → 回退 negotiation.editable（数据列只读 + 回复/意见可编辑）
          editable={!itemProps?.view || (n?.mode === 'external' && n.editable)}
          // 场景模式：谈判上下文透传（外包 external=数据列只读+回复/意见可编辑；内包 internal=数据列可编辑+回复只读）
          mode={n?.mode}
          editState={n?.editState}
          onEditChange={n?.onEditChange}
          onBatchAction={n?.onBatchAction}
          // 谈判视角：追加回复/意见反馈列（showNegotiationColumns 由调用方注入；外包恒真）
          showNegotiationColumns={renderData?.showNegotiationColumns}
          // 费用预算联动（付款类型可选项过滤 + LF/MG 金额汇总校验）
          budgetConfig={paymentPlanBudgetConfig}
          // 付款金额列币种：同步费用预算预算总金额币种（$budget_total_amount formItemKeyMap 拆分后的 currency，只读不可改）
          currency={renderData?.formData?.currency}
          paymentNodeOptions={renderData?.optionMap?.paymentNodeOptions ?? []}
        />
      );
    },
  },
});

export default IPCooperationDemandFormModel;
